import React, {FC, useState} from 'react';
import {Button, InputAdornment} from "@mui/material";
import { SidePanelContent, SidePanelFooter } from '@securspace/securspace-ui-kit';
import AccountTypeSelect from "./AccountTypeSelect";
import {
  requestAddPaymentMethod,
  requestToken
} from "../../components/paymentMethods/addPaymentMethod/paymentMethodRequests";
import {getErrorMessage} from "../../util/NetworkErrorUtil";
import Busy from "../../components/Busy";
import DwollaService from "../../components/paymentMethods/processors/DwollaService";
import {AccountTypes} from "../../components/constants/securspace-constants";
import BankAccountConfirmation from "../../components/paymentMethods/addPaymentMethod/BankAccountConfirmation";
import {
  AccountIcon,
  AppInput,
  PaddingContainer,
  PersonIcon,
  RouteLabel,
} from './styles/bankInfoStyles';
import {useSnackbar} from "../../context/snackbar-context";
import type {Account} from "../../types/Account";

interface IRedesignMicroDepositFormProps {
  account: Account;
  setShowMicroDepositVerify: (show: boolean) => void;
  handleModalClose: () => void;
}

const RedesignMicroDepositForm: FC<IRedesignMicroDepositFormProps> = (props: IRedesignMicroDepositFormProps) => {
  const {account, setShowMicroDepositVerify, handleModalClose} = props;
  const {snackbarShowMessage} = useSnackbar();
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountNumberVerify, setAccountNumberVerify] = useState('');
  const [accountType, setAccountType] = useState('');
  const [accountAddedSuccessfully, setAccountAddedSuccessfully] = useState(false);
  const [accountHolderName, setAccountHolderName] = useState('');
  const dwolla = new DwollaService({dwollaEnv: account.dwollaEnv});

  const handleRoutingNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRoutingNumber(event.target.value);
  }
  const handleAccountNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAccountHolderName(event.target.value);
  }

  const handleAccountNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAccountNumber(event.target.value);
  }

  const handleAccountNumberVerifyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAccountNumberVerify(event.target.value);
  }

  const handleAccountTypeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setAccountType(event.target.value);
  }

  const onSubmit = () => {
    if ((accountNumber.length >= 4) && (accountNumber !== accountNumberVerify)) {
      alert('Account numbers do not match');
      return;
    }
    const bankAccountLastFour = accountNumber.substring(accountNumber.length - 4, accountNumber.length);
    const checkingOrSavings = accountType === AccountTypes.COMPANY_CHECKING || accountType === AccountTypes.INDIVIDUAL_CHECKING ? "checking" : "savings";
    Busy.set(true);
    requestToken(account)
      .then((resp) => {
        const data = {
          authorityId: account.id,
          userType: account.userType,
          routingNumber: routingNumber,
          accountNumber: accountNumber,
          accountType: checkingOrSavings,
          bankAccountLastFour: bankAccountLastFour,
          token: resp.body.token
        }
        const savePaymentMethod = (dwollaFundingSourceId: string) => {
          const paymentAdd = JSON.stringify({
            accountId: account.id,
            paymentProcessor: 'DWOLLA',
            bankAccountNickName: bankAccountLastFour,
            dwollaFundingSourceId: dwollaFundingSourceId
          })
          requestAddPaymentMethod(paymentAdd)
            .then(() => {
              setAccountAddedSuccessfully(true);
            })
            .catch((error) => {
              snackbarShowMessage(getErrorMessage(error, "Unable to save the bank account. Please try again."), 'error', 15000);
            })
        };
        dwolla.createDwollaFundingSource(data, savePaymentMethod, (error: string, existingFundingSourceId?: string) => {
          if (existingFundingSourceId) {
            // Dwolla already has this bank. The API links it if it is ours and not on file yet,
            // otherwise it answers with the "already exists" message.
            savePaymentMethod(existingFundingSourceId);
            return;
          }
          if (error.toLowerCase().startsWith("bank already exists")) {
            // Avoid showing user id in error message
            snackbarShowMessage("A bank account with the provided routing and account number already exists for your account.", 'error', 15000);
            return;
          }
          snackbarShowMessage(String(error), 'error', 15000);
        })
      }).catch((error) => {
      snackbarShowMessage(getErrorMessage(error, "Unable to add the bank account. Please try again."), 'error', 15000);
    }).finally(() => {
      Busy.set(false);
    });
  }

  return <>
    {!accountAddedSuccessfully ?
      <>
        <SidePanelContent>
          <PaddingContainer>
            <RouteLabel variant='h6'>
              Enter Bank Account Details
            </RouteLabel>
            <AppInput
              type='text'
              onChange={handleAccountNameChange}
              value={accountHolderName}
              placeholder='Enter the full name of the person who holds this account'
              fullWidth
            label="Account Holder Full Name"
            InputLabelProps={{
              shrink: true,
            }}
            variant='standard'
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <PersonIcon fontSize='small'/>
                </InputAdornment>
              ),
            }}
          />
          <AccountTypeSelect accountType={accountType} handleAccountTypeChange={handleAccountTypeChange}/>
          <AppInput
            type='text'
            onChange={handleRoutingNumberChange}
            placeholder='1100000000'
            fullWidth
            label="Routing Number"
            InputLabelProps={{
              shrink: true,
            }}
            variant='standard'
          />
          <AppInput
            type='text'
            onChange={handleAccountNumberChange}
            placeholder='1122334455666'
            fullWidth
            label="Account Number"
            InputLabelProps={{
              shrink: true,
            }}
            variant='standard'
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <AccountIcon src="/app-images/AccountBalance.png" alt="Account Icon"/>
                </InputAdornment>
              ),
            }}
          />
          <AppInput
            type='text'
            onChange={handleAccountNumberVerifyChange}
            placeholder='1122334455666'
            fullWidth
            label="Re-enter Account Number"
            InputLabelProps={{
              shrink: true,
            }}
            variant='standard'
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <AccountIcon src="/app-images/AccountBalance.png" alt="Account Icon"/>
                </InputAdornment>
              ),
            }}
          />
        </PaddingContainer>
        </SidePanelContent>

        <SidePanelFooter>
          <Button variant='text' color='secondary' onClick={() => setShowMicroDepositVerify(false)}>
            Back
          </Button>
          <Button variant='text' color='secondary' onClick={onSubmit}>
            Next
          </Button>
        </SidePanelFooter>
      </> :
      <BankAccountConfirmation
        handleModalClose={handleModalClose}
      />
    }
  </>
};

export default RedesignMicroDepositForm;
